import React, { useState } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  MenuItem,
  useTheme,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItemText,
  ListItemButton,
  Divider,
  Typography,
  Button,
  Badge,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { getAppBarStyles, toolbarStyles } from "../styles/NavBarStyles";
import { NavBarLogo } from "../components/NavBarLogo";
import { NavBarTitle } from "../components/NavBarTitle";
import { LogoutButton } from "../components/LogoutButton";
import ThemeToggle from "../components/ThemeToggle";
import SoporteModal from "../../modals/soporte";
import { useNavBar } from "./useNavBar";
import NavMenu from "./NavMenu";
import { useAppTheme } from "../../../context/theme.context";
import {
  DEALERSHIP_NAV_SECTIONS,
  getVisibleNavSections,
  isRouteAllowed,
} from "../../../utils/routing/routePermissions";

/**
 * GUARD-004: el navbar desktop y móvil se generan desde la misma configuración
 * de rutas/permisos (DEALERSHIP_NAV_SECTIONS), filtrada por los permisos del
 * usuario. Ambos viewports muestran exactamente el mismo conjunto autorizado.
 */
const NavBar = () => {
  const {
    currentUser,
    anchorEl,
    isSoporteModalOpen,
    pendingLoanStarts,
    handleMenuToggle,
    handleMenuClose,
    handleNavigate,
    handleLogout,
    handleOpenSoporteModal,
    handleCloseSoporteModal,
    beginUnitSelection,
    canChangeWorkspace,
  } = useNavBar();

  const { colors } = useAppTheme();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const visibleSections = getVisibleNavSections(
    DEALERSHIP_NAV_SECTIONS,
    currentUser,
  );

  const renderLoanLabel = (item) => {
    if (item.path === "/loans" && pendingLoanStarts > 0) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
          <Box component="span" sx={{ flexGrow: 1 }}>
            {item.label}
          </Box>
          <Badge
            badgeContent={pendingLoanStarts}
            color="error"
            title={`${pendingLoanStarts} préstamo(s) sin comenzar`}
          />
        </Box>
      );
    }
    return item.label;
  };

  return (
    <Box sx={{ flexGrow: 1, mb: 3 }}>
      <AppBar position="static" sx={getAppBarStyles(colors)}>
        <Toolbar sx={toolbarStyles}>
          <NavBarLogo onHomeClick={() => handleNavigate("/")} />

          <NavBarTitle
            title={currentUser?.dealership || currentUser?.activeUnitName}
          />

          {canChangeWorkspace && !isMobile && (
            <Button
              color="inherit"
              size="small"
              startIcon={<StorefrontIcon />}
              onClick={beginUnitSelection}
            >
              Cambiar sucursal
            </Button>
          )}

          {!isMobile ? (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {visibleSections.map((section) => (
                <NavMenu
                  key={section.id}
                  menuId={`${section.id}-menu`}
                  label={section.label}
                  anchorEl={anchorEl[`${section.id}-menu`]}
                  onMenuToggle={handleMenuToggle}
                  onMenuLeave={handleMenuClose}
                >
                  {section.items.map((item) => (
                    <MenuItem
                      key={item.path}
                      onClick={() =>
                        handleNavigate(item.path, `${section.id}-menu`)
                      }
                    >
                      {renderLoanLabel(item)}
                    </MenuItem>
                  ))}
                </NavMenu>
              ))}
            </Box>
          ) : (
            <IconButton color="inherit" onClick={() => setMobileMenuOpen(true)}>
              <MenuIcon />
            </IconButton>
          )}

          <ThemeToggle />
          <LogoutButton onLogout={handleLogout} />
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            width: 300,
            backgroundColor: colors.surface || "#fff",
            backgroundImage: "none",
          },
        }}
      >
        {/* Drawer header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 2,
            background: colors.navBar?.gradient || colors.primary,
            color: "#fff",
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {currentUser?.dealership || currentUser?.activeUnitName || "Menú"}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setMobileMenuOpen(false)}
            sx={{ color: "#fff" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <List sx={{ pt: 1 }}>
          {/* Home */}
          {isRouteAllowed("/", currentUser) && (
            <ListItemButton
              onClick={() => {
                setMobileMenuOpen(false);
                handleNavigate("/");
              }}
              sx={{ borderRadius: 2, mx: 1, mb: 0.5 }}
            >
              <ListItemText
                primary="Inicio"
                primaryTypographyProps={{ fontWeight: 600 }}
              />
            </ListItemButton>
          )}

          {visibleSections.map((section, index) => (
            <Box key={section.id}>
              {index > 0 && <Divider sx={{ my: 1.5, mx: 2 }} />}
              <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.textSecondary || "text.secondary",
                    fontWeight: 700,
                    fontSize: "0.7rem",
                    letterSpacing: 1.2,
                  }}
                >
                  {section.label}
                </Typography>
              </Box>
              {section.items.map((item) => (
                <ListItemButton
                  key={item.path}
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleNavigate(item.path);
                  }}
                  sx={{ borderRadius: 2, mx: 1, mb: 0.5 }}
                >
                  <ListItemText primary={renderLoanLabel(item)} />
                </ListItemButton>
              ))}
            </Box>
          ))}

          <Divider sx={{ my: 1.5, mx: 2 }} />
          {canChangeWorkspace && (
            <ListItemButton
              onClick={() => {
                setMobileMenuOpen(false);
                beginUnitSelection();
              }}
              sx={{ borderRadius: 2, mx: 1, mb: 0.5 }}
            >
              <ListItemText primary="Cambiar sucursal" />
            </ListItemButton>
          )}
          <ListItemButton
            onClick={() => {
              setMobileMenuOpen(false);
              handleOpenSoporteModal();
            }}
            sx={{ borderRadius: 2, mx: 1, mb: 0.5 }}
          >
            <ListItemText primary="Soporte" />
          </ListItemButton>
        </List>
      </Drawer>

      <SoporteModal
        open={isSoporteModalOpen}
        handleClose={handleCloseSoporteModal}
      />
    </Box>
  );
};

export default NavBar;
